/* eslint-disable no-debugger */
import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';
import { useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './MemberCoursesDetailValidation';

import usePersonCoursesData from '../../../../state/member/memberCourses/UsePersonCoursesData';
import usePersonCoursesForAdminRoleData from '../../../../state/member/memberAdminCourses/UsePersonCoursesForAdminRoleData';
import useNavRoutes from '@usas/ui-web/state/security/UseNavRoutes';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const INITIAL_STATE = {
  view: '',
  viewService: '',
  course: {},
  personId: '',
  apiErrorMessage: ''
};

const INITIAL_FORM_STATE = {
  courseName: '',
  lmsCourseId: Constants.DEFAULT_ID,
  courseCompletionDate: Constants.BLANK_DATE_STRING,
  courseExpirationDate: Constants.BLANK_DATE_STRING,
  noExpirationDate: false,
  durationInMonths: 0
};

const ADVANCED_WRITE_VIEW_CONSTANT = 'HQ';

const API_ERROR_MESSAGE = 'We are unable to complete your request at this time. Please try again later.';

const useMemberCoursesDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navRoutes } = useNavRoutes();
  const { memberPostCoursesAdminState, memberPutCoursesAdminState, postEnrollmentMemberCoursesAdmin,
    putMemberCourseEnrollmentAdmin
  } = usePersonCoursesData();
  const { memberCoursesAdminRoleState } = usePersonCoursesForAdminRoleData();
  const { formState, errorState, setFormData, setFormState, onFormValueChanged, handleSubmit, onValueTextPairChanged } = useForm(INITIAL_FORM_STATE, submitFormCallback, validate);
  const [state, setState] = useState(INITIAL_STATE);

  const onBackToMemberAllCourseWrite = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (state.view === ADVANCED_WRITE_VIEW_CONSTANT) {
      navigate(navRoutes?.MEMBER_ALLCOURSES_ADVANCED?.route);
    } else {
      navigate(navRoutes?.MEMBER_ALLCOURSES?.route);
    }
  };

  const getCourseExpirationDate = () => {
    return formState.noExpirationDate === false ? Constants.DEFAULT_MAX_DATE : formState.courseExpirationDate;
  };

  const getAddOrEdit = (course) => {
    return course === undefined ? 'Add' : 'Edit';
  };

  const onReloadClicked = () => {
    window.location.reload();
  }

  function addMonthsToDate(date, months) {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return formatDate(d);
  }

  function isValidDate(dateString) {
    if (typeof dateString !== 'string') {
      return false;
    }

    const value = dateString.trim();

    if (value === '' || value === Constants.DEFAULT_DATE) {
      return false;
    }

    const parsed = Date.parse(value);
    return !Number.isNaN(parsed);
  }

  useEffect(() => {
    if (memberCoursesAdminRoleState?.isArrayLoaded === true &&
      (formState.lmsCourseId !== Constants.DEFAULT_ID && formState.lmsCourseId !== undefined) &&
      state.viewService === "Add") {

      let duration = memberCoursesAdminRoleState?.arrayData?.find(item => item.lmsCourseId === formState.lmsCourseId)?.durationInMonths || 0;

      setFormState(prev => ({
        ...prev,
        courseCompletionDate: Constants.BLANK_DATE_STRING,
        courseExpirationDate: Constants.BLANK_DATE_STRING,
        noExpirationDate: duration === 0 ? false : true,
        durationInMonths: duration
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberCoursesAdminRoleState?.isArrayLoaded, formState.lmsCourseId]);

  useEffect(() => {
    if (isValidDate(formState.courseCompletionDate) === true && formState.durationInMonths !== 0) {
      setFormState(prev => ({
        ...prev,
        courseExpirationDate: addMonthsToDate(formState.courseCompletionDate, formState.durationInMonths)
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.courseCompletionDate]);

  useEffect(() => {
    if (state.viewService === 'Edit') {
      const course = location.state?.course;

      setFormData({
        ...formState,
        courseName: course?.courseName,
        lmsCourseId: course?.lmsCourseId,
        courseCompletionDate: formatDate(course?.courseCompletionDate),
        courseExpirationDate: formatDate(course?.courseExpirationDate),
        noExpirationDate: formatDate(course?.courseExpirationDate) === Constants?.DEFAULT_MAX_DATE ? false : true,
        courseEnrollmentDate: formatDate(course?.courseEnrollmentDate),
        courseEnrollmentId: course?.courseEnrollmentId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.viewService]);

  useEffect(() => {
    if (location.state) {
      const course = location.state?.course;

      setState({
        ...state,
        view: location.state?.viewLocation,
        viewService: getAddOrEdit(course),
        personId: location.state?.personId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submitFormCallback() {
    const personId = state.personId;
    const courseExpirationDate = getCourseExpirationDate();

    if (state.viewService === "Add") {
      const postObj = {
        personId: personId,
        lmsCourseId: formState.lmsCourseId,
        courseEnrollmentDate: formState.courseCompletionDate,
        courseCompletionDate: formState.courseCompletionDate,
        courseExpirationDate: courseExpirationDate,
        coursePassed: true
      };

      const postEnrollmentPromise = postEnrollmentMemberCoursesAdmin(postObj);

      if (postEnrollmentPromise ?? false) {
        postEnrollmentPromise.then((newState) => {
          if (newState ?? false) {
            onBackToMemberAllCourseWrite();
          }
        }).catch(() => {
          setState({
            ...state,
            apiErrorMessage: API_ERROR_MESSAGE
          });
        });
      }
    } else {
      const courseId = formState.courseEnrollmentId;
      const putObject = {
        courseEnrollmentId: formState.courseEnrollmentId,
        courseEnrollmentDate: formState.courseEnrollmentDate,
        courseCompletionDate: formState.courseCompletionDate,
        courseExpirationDate: courseExpirationDate,
        coursePassed: true
      };

      const putEnrollmentPromise = putMemberCourseEnrollmentAdmin(courseId, putObject);

      if (putEnrollmentPromise ?? false) {
        putEnrollmentPromise.then((newState) => {
          if (newState ?? false) {
            onBackToMemberAllCourseWrite();
          }
        }).catch(() => {
          setState({
            ...state,
            apiErrorMessage: API_ERROR_MESSAGE
          });
        });
      }
    }
  }

  return {
    isSaving: memberCoursesAdminRoleState.isArrayLoading || memberPostCoursesAdminState.isObjLoading || memberPutCoursesAdminState.isObjLoading,
    view: state.view,
    viewService: state.viewService,
    apiErrorMessage: state.apiErrorMessage,
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    onBackToMemberAllCourseWrite,
    onReloadClicked
  };
};

export default useMemberCoursesDetail;